import { Module } from 'vuex'

import { ConfigStateModuleName, NotificationModuleName } from '../modules'

import { CheckSessionUseCase } from '@/application/auth/checkSessionUseCase'
import { LoginUseCase } from '@/application/auth/loginUseCase'
import { LogoutUseCase } from '@/application/auth/logoutUseCase'
import { User } from '@/domain/entities/user/user'
import { UserRegistrationRequest } from '@/domain/entities/user/userCreateRequest'
import { AuthRepository } from '@/domain/repositories/auth/AuthRepository'

interface AuthState {
  user: User
  isLogin: boolean
  isLoading: boolean
  error: Error | null
}

export const authStateModule = (authRepository: AuthRepository): Module<AuthState, any> => {
  const checkSessionUseCase = new CheckSessionUseCase(authRepository)
  const loginUseCase = new LoginUseCase(authRepository)
  const logoutUseCase = new LogoutUseCase(authRepository)

  return {
    namespaced: true,
    state: {
      user: {
        id: '',
        name: '',
        avatar: null,
        policy: 'private',
        country: 'Japan',
        displayLanguage: 'English',
        programLanguage: 'C',
        status: null,
        registerDate: new Date(),
        affiliation: null,
        birthYear: null,
        lastSubmitDate: null,
        url: null
      },
      isLogin: false,
      isLoading: false,
      error: null
    },
    mutations: {
      setUser(state, { user }: { user: User }) {
        state.user = user
      },
      setIsLogin(state, isLogin: boolean) {
        state.isLogin = isLogin
      },
      setIsLoading(state, isLoading: boolean) {
        state.isLoading = isLoading
      },
      setError(state, error: Error | null) {
        state.error = error
      }
    },
    actions: {
      async checkSession({ state, commit }) {
        commit('setIsLoading', true)
        commit('setError', null)

        try {
          const user = await checkSessionUseCase.checkSession()
          if ('id' in user) {
            commit('setIsLogin', true)
          }
          commit('setUser', { user })
          commit(
            `${ConfigStateModuleName}/setDisplayLanguage`,
            { displayLanguage: user.displayLanguage },
            { root: true }
          )
          commit(
            `${ConfigStateModuleName}/setProgramLanguage`,
            { programLanguage: user.programLanguage },
            { root: true }
          )
        } catch (error) {
          commit('setIsLogin', false)
          commit('setError', error)
        } finally {
          commit('setIsLoading', false)
        }
      },
      async login({ commit }, { id, password }) {
        commit('setIsLoading', true)
        commit('setError', null)

        try {
          const user = await loginUseCase.login(id, password)
          if ('id' in user) {
            commit('setIsLogin', true)
          }
          commit('setUser', { user })
          commit(
            `${ConfigStateModuleName}/setDisplayLanguage`,
            { displayLanguage: user.displayLanguage },
            { root: true }
          )
          commit(
            `${ConfigStateModuleName}/setProgramLanguage`,
            { programLanguage: user.programLanguage },
            { root: true }
          )
        } catch (error) {
          commit('setIsLogin', false)
          commit('setError', error)
        } finally {
          commit('setIsLoading', false)
        }
      },
      async logout({ commit }) {
        commit('setIsLoading', true)
        commit('setError', null)

        try {
          await logoutUseCase.logout()
          commit('setIsLogin', false)
        } catch (error) {
          commit('setError', error)
        } finally {
          commit('setIsLoading', false)
        }
      },
      async registerUser({ commit, dispatch, rootGetters }, { user }: { user: UserRegistrationRequest }) {
        commit('setIsLoading', true)
        commit('setError', null)

        try {
          await authRepository.registerUser(user)
          await dispatch(`login`, { id: user.id, password: user.password })
          await dispatch(`${NotificationModuleName}/signupNotification`, { userId: user.id }, { root: true })
          setTimeout(() => {
            window.location.reload()
          }, 2000)
        } catch (error) {
          commit('setError', error)
          const displayLanguage = rootGetters[`${ConfigStateModuleName}/displayLanguage`]
          await dispatch(
            `${NotificationModuleName}/errorNotification`,
            { error: displayLanguage == 'Japanese' ? 'アカウント登録に失敗しました' : 'Failed to register account' },
            { root: true }
          )
        } finally {
          commit('setIsLoading', false)
        }
      }
    },
    getters: {
      user: state => state.user,
      isLogin: state => state.isLogin,
      isLoading: state => state.isLoading,
      error: state => state.error
    }
  }
}
